import { useEffect, useState } from 'react';
import api from '../../api/axios';
import Modal from '../../components/common/AdminModal';
import EmailTemplateForm from '../../components/admin/EmailTemplateForm';
import { Pencil } from 'lucide-react';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import Toast from '../../components/common/Toast';
import { useMainCompany } from '../../context/MainCompanyContext';

export default function AdminEmailTemplates() {
  const { selectedMain } = useMainCompany();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ type: null, data: null }); // type: create|edit
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState(null);
  // const [message, setMessage] = useState(null);
  // local toast
  const [toast, setToast] = useState({ message: '', type: 'success', visible: false });
  const showToast = (msg, type = 'success', duration = 4000) => {
    setToast({ message: msg, type, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  };

  // main companies and selectedMain are provided by MainCompanyContext

  useEffect(() => {
    const loadTemplates = async () => {
      if (!selectedMain) return setTemplates([]);
      setLoading(true);
      try {
        const resp = await api.get(`/admin/email-templates/${selectedMain}`);
        const body = resp.data;
        const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
        setTemplates(items);
      } catch (e) {
        console.error('Failed to load templates', e?.response?.data || e?.message);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };
    loadTemplates();
  }, [selectedMain]);

  const openEdit = (t) => setModal({ type: 'edit', data: t });

  const handleSaved = async () => {
    setModal({ type: null, data: null });
    // refresh
    try {
      const resp = await api.get(`/admin/email-templates/${selectedMain}`);
      const body = resp.data;
      const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
      setTemplates(items);
      showToast('Saved successfully', 'success');
    } catch (e) {
      console.error('Failed reload after save', e?.response?.data || e?.message);
      const msg = e?.response?.data?.message || e?.response?.data || e?.message || 'Failed to reload templates after save';
      showToast(msg, 'error');
    }
  };

  // confirmDelete removed (delete UI currently commented out)

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/email-templates/${deleteTarget.id}`, { data: { password: deletePassword }, skipAuthRedirect: true });
      setShowDelete(false);
      setDeleteTarget(null);
      // refresh list
      const resp = await api.get(`/admin/email-templates/${selectedMain}`);
      const body = resp.data;
      const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
  setTemplates(items);
  showToast('Deleted successfully', 'success');
    } catch (e) {
      console.error('Delete failed', e?.response?.data || e?.message);
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.response?.data || e?.message || 'Delete failed';
  setDeleteError(msg || (status === 403 ? 'Invalid delete password' : msg));
  showToast(msg, 'error');
    }
  };

  return (
    <div className="p-4">
      <AdminPageHeader
        title="Email Templates"
        actions={(
          <div className="flex items-center gap-2" />
        )}
      />

  <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      <div className="overflow-x-auto border rounded bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700"><tr><th className="px-2 py-2 text-left">Subject</th><th className="px-2 py-2 text-left">Actions</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={2} className="text-center py-4 text-gray-500">Loading...</td></tr>
            ) : templates.length ? (
              templates.map(t => (
                <tr key={t.id} className="border-t hover:bg-gray-50">
                  <td className="px-2 py-2">{t.subject}</td>
                  <td className="px-2 py-2">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(t)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"><Pencil className="w-3 h-3"/> Edit</button>
                    {/*  <button onClick={() => confirmDelete(t)} className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 flex items-center gap-1"><Trash className="w-3 h-3"/> Delete</button> */}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={2} className="text-center py-3 text-gray-500">No templates found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal for create/edit */}
      {modal.type && (
        <Modal title={modal.type === 'create' ? 'Add Email Template' : 'Edit Email Template'} size="4xl" onClose={() => setModal({ type: null, data: null })} maxWidth="max-w-3xl">
          <EmailTemplateForm
            mode={modal.type}
            mainCompanyId={selectedMain}
            initial={modal.data}
            onClose={() => setModal({ type: null, data: null })}
            onSaved={handleSaved}
          />
        </Modal>
      )}

      {/* Delete modal */}
      {showDelete && (
        <Modal title="Delete Email Template" onClose={() => setShowDelete(false)}>
          <div>
            <p>Are you sure you want to delete <strong>{deleteTarget?.subject}</strong>?</p>
            <div className="mt-3">
              <label className="block text-sm">Enter delete password</label>
              <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} className="w-full border px-2 py-1 rounded mt-1" />
              {deleteError && <div className="text-red-600 text-sm mt-2">{deleteError}</div>}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowDelete(false)} className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400">Cancel</button>
              <button onClick={doDelete} className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
